import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row, Col } from 'react-bootstrap';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { throttle as _throttle } from 'lodash';

import { dispatchResizeEvent } from 'helpers/window';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';
import RichEditorContent from 'components/RichEditorContent';

function CmsElementHero(props) {
  const { editMode, index, showElementSettings } = props;

  // Access Redux layout state
  const layout = useSelector((state) => state.layout);

  // Local state for manual parallax and visibility
  const [parallaxState, setParallaxState] = useState({ translateY: 0, opacity: 1 });

  // Refs for DOM access (Replacing ReactDOM.findDOMNode)
  const containerRef = useRef(null);

  // CMS Logic via Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses,
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index,
    disabled: !editMode,
  });

  /**
   * Manual parallax calculation logic
   */
  const handleScroll = useCallback(
    _throttle(() => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const containerHeight = rect.bottom - rect.top;

      // Calculate opacity based on how much of the element is scrolled off-screen
      const scrolledAmount = rect.top < 0 ? Math.abs(rect.top) : 0;
      const opacity = (containerHeight - scrolledAmount) / containerHeight;

      setParallaxState({
        translateY: (1 - opacity) * containerHeight * 0.3,
        opacity: opacity * 1.5,
      });
    }, 50),
    []
  );

  /**
   * Lifecycle effects
   */
  useEffect(() => {
    // Add scroll listener for manual parallax
    window.addEventListener('scroll', handleScroll);

    // Preload background image to trigger layout recalculation
    const backgroundUrl = getParamValue('backgroundUrl');
    if (backgroundUrl) {
      const img = new Image();
      img.onload = () => dispatchResizeEvent();
      img.src = backgroundUrl;
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [getParamValue, handleScroll]);

  // dnd-kit Styles
  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    width: '100%',
    cursor: editMode ? 'move' : 'default',
  };

  // Header styles including dynamic height from Redux
  const isFullHeight = getParamValue('fullHeight', 'no') === 'yes';
  const headerStyle = {
    height: isFullHeight ? `${layout.height}px` : 'auto',
  };

  const content = getParamValue('content', '');
  const shownClass = shown ? 'elem-shown ' : 'elem-hidden ';
  const vAlignClass = getParamValue('vAlignMiddle', false) ? 'col-middle ' : '';
  const mainColor = getParamValue('mainColor', 'master');

  const innerContent = (
    <div
      className="full-height no-padding"
      ref={containerRef}
      onClick={(e) => editMode && showElementSettings(e, index)}
    >
      <div
        id={getMainId()}
        className={`ms-hero container-fluid container-fixed-lg sm-p-l-20 sm-p-r-20 ${shownClass} ${cssClasses} ${responsiveClasses}`}
        style={{
          ...headerStyle,
          ...getBackgroundStyle(),
        }}
      >
        {elementStyleTag}

        <div className="content inner full-height">
          <div className="container-xs-height full-height">
            <div className={`col-xs-height ${vAlignClass}`}>
              {content !== '' ? (
                <Container>
                  <Row>
                    <Col xs={12} className={`text-${mainColor}`}>
                      <RichEditorContent content={`<div>${content}</div>`} />
                    </Col>
                  </Row>
                </Container>
              ) : (
                <div className={`p-t-15 p-b-15 text-center text-${mainColor}`}>
                  <i className="fa fa-newspaper-o fa-4x" />
                </div>
              )}
            </div>
          </div>
        </div>

        {getAppendHtml()}
      </div>
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    <div ref={setNodeRef} style={dndStyle} {...attributes} {...listeners}>
      <div>
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>
              {innerContent}
            </Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>

      </div>
    </div>
  );
}

export default CmsElementHero;
